import { ChatOption } from '../../services/local-chat.service';

export interface ChatBubble {
  kind: 'bot' | 'user';
  text: string;
  textKey?: string;
  params?: Record<string, unknown>;
}

export interface ChatTyping {
  kind: 'typing';
}

export type ChatItem = ChatBubble | ChatTyping;

export interface ChatChip {
  id: string;
  label: string;
  targetNodeId?: string;
  parentNodeId?: string;
}

export interface CategorySnapshot {
  items: ChatItem[];
  chips: ChatChip[];
  visitedNodeIds: string[];
  childNodeCount: number;
  excludedNodeCount: number;
  currentNodeId: string;
}

export type WidgetState = 'closed' | 'opened' | 'minimized';

export type FailedRequest =
  | { kind: 'start' }
  | { kind: 'select'; chip: ChatChip };

export type ChipIntent =
  | 'root-reset'
  | 'category-change'
  | 'static-yes'
  | 'static-no'
  | 'static-close'
  | 'leaf-selection';

export const RETRY_CHIP_ID = '__retry__';
export const CLOSE_CHIP_ID = '__close__';

export const TYPING_ITEM: ChatItem = { kind: 'typing' };

export const CATEGORY_REENTRY_MESSAGE = 'Here are more questions you can explore. Tap one to continue.';

const STATICYES = 'static-yes';
const STATICNO = 'static-no';
const ROOT = 'root';

const QUESTION_LABELS: Record<string, string> = {
  copies: 'Will I receive copies after I sign?',
  who: 'Who needs to sign the closing documents?',
  proof: 'Do I need to show my ID?',
  sessiontake: 'How long does the closing usually take?',
  nbthsign: 'Why does an NBTH need to sign?',
  loaninfo: 'What are the terms of my loan?',
  payoffs: 'What about payoff and creditor payments?',
  escrow: 'What happens to my escrow account?',
  autopay: 'How do I set up autopay?',
  discrepancies: 'What if my loan terms are incorrect?',
  proceeds: 'Do I need to enter bank details?',
  incorrect: 'What if my address is incorrect?',
  morethaniowe: 'What if a creditor amount is higher than my balance?',
  fundsdueatclosing: 'How do I make a payment for funds due at closing?',
};

const CATEGORY_LABELS: Record<string, string> = {
  signing: 'Signing documents',
  loan: 'Loan and financial information',
};

export function getQuestionLabel(nodeId: string): string {
  return QUESTION_LABELS[nodeId] ?? nodeId;
}

export function getCategoryLabel(nodeId: string): string {
  return CATEGORY_LABELS[nodeId] ?? nodeId;
}

export function classifyChip(chip: ChatChip, rootNodeTargetId: string): ChipIntent {
  if (chip.parentNodeId === ROOT && chip.targetNodeId === ROOT) {
    return 'root-reset';
  }
  if (chip.id === STATICYES) {
    return STATICYES;
  }
  if (chip.id === STATICNO) {
    return STATICNO;
  }
  if (chip.id === CLOSE_CHIP_ID) {
    return 'static-close';
  }
  if (chip.parentNodeId === ROOT) {
    return 'category-change';
  }
  void rootNodeTargetId;
  return 'leaf-selection';
}

export function buildConversationContext(items: ChatItem[]): string | null {
  const bubbles = items.filter(
    (item): item is { kind: 'bot' | 'user'; text: string } =>
      item.kind === 'bot' || item.kind === 'user'
  );
  if (bubbles.length === 0) return null;
  return bubbles
    .slice(-10)
    .map((b) => `${b.kind === 'bot' ? 'Assistant' : 'User'}: ${b.text}`)
    .join('\n');
}

export function mapOptionsToChips(
  options: ChatOption[],
  currentNodeId: string,
  visitedNodeIds: readonly string[],
  excludeNodeIds: readonly string[] = []
): ChatChip[] {
  return options
    .filter((option) => !visitedNodeIds.includes(option.targetNodeId))
    .filter((option) => !excludeNodeIds.includes(option.targetNodeId))
    .map((option, index) => ({
      id: `opt-${index}-${option.targetNodeId}`,
      label: CATEGORY_LABELS[option.targetNodeId] ?? getQuestionLabel(option.targetNodeId),
      targetNodeId: option.targetNodeId,
      parentNodeId: currentNodeId,
    }));
}

export function createDisplayBubble(
  kind: 'bot' | 'user',
  text: string,
  params?: Record<string, unknown>
): ChatBubble {
  return { kind, text, params };
}

export function buildFollowUpChips(categoryNodeId: string): ChatChip[] {
  return [
    { id: STATICYES, label: 'Yes', parentNodeId: ROOT, targetNodeId: categoryNodeId },
    { id: STATICNO, label: 'No', parentNodeId: ROOT, targetNodeId: categoryNodeId },
  ];
}

export function hasRemainingChildren(
  childNodeCount: number,
  visitedCount: number,
  excludedCount = 0
): boolean {
  return childNodeCount - excludedCount > visitedCount;
}
